import {Lot} from "../common/types/Types";
import {EntitySelect} from "../common/inputs/EntitySelect";
import {BYTable} from "../common/views/BYTable";
import {CommonHelper} from "../common/CommonHelper";
import React, {useState} from "react";
import {BYInput} from "../common/inputs/BYInput";
import BYRangeDatePicker from "../common/inputs/BYRangeDatePicker";
import BYExportButtons from "../common/buttons/BYExportButtons";
import {useThrottledLayoutEffect} from "../api/hooks";

interface LotListProps {
    data: Lot[];
    loading?: boolean;
}

const LotList = (props: LotListProps) => {
    const {data, loading}  = props;
    const [searchText, setSearchText] = useState('');
    const [rangeDates, setRangeDates] = useState([null, null]);
    const [selectedBuildings, setSelectedBuildings] = useState<string[]>([]);
    const [selectedPhases, setSelectedPhases] = useState<number[]>([]);
    const [filteredLots, setFilteredLots] = useState<Lot[]>(data);
    const [selectedLots, setSelectedLots] = useState<Lot[]>([]);

    useThrottledLayoutEffect(() => {
        if (!data) {
            return;
        }
        const newLots = data.filter((lot) => {
            const [fromDate, toDate] = rangeDates;
            const code = `${lot.numeroLot}`.toUpperCase();
            const reference = `${lot.acquereur?.nom} ${lot.acquereur?.prenom}`.toUpperCase();
            // const city = `${user.adresse} ${user.ville} ${user.cp}`.toUpperCase();
            const searchTextUpperCased = searchText.toUpperCase();

            const hasBuilding = !selectedBuildings.length || selectedBuildings.includes(lot.batiment);
            const hasVisite = (!fromDate && !toDate) || lot.lotVisites.some((lv) => {

                if (!lv.dateLotVisite) {
                    return false;
                }

                const dateVisite = new Date(lv.dateLotVisite);

                return (!fromDate || fromDate < dateVisite) &&
                    (!toDate || toDate > dateVisite);
            });
            const hasPhase = !selectedPhases.length || lot.reservePhases.some((phaseId) => selectedPhases.includes(phaseId));

            return (code.includes(searchTextUpperCased) || reference.includes(searchTextUpperCased)) && hasBuilding && hasVisite && hasPhase;
        });

        setFilteredLots(newLots);
    }, 300,[searchText, data, selectedBuildings, selectedPhases, rangeDates]);

    const operationId = data.length ? data[0].operationRefOperation : undefined;

    return <>
        <div className="row mb-3">
            <div className="col-3">
                <BYInput type="text"
                       title="Rechercher"
                       placeholder="Numéro de lot, nom/prénom acquéreur, etc."
                       className="form-control"
                       value={searchText}
                       onChange={(e) => setSearchText(e.target.value)}/>
            </div>
            <div className="col-3">
                <BYRangeDatePicker
                    title={'Date de visite'} value={rangeDates}
                    onChange={(newRangeDates) => {
                        setRangeDates(newRangeDates);
                    }}
                />
            </div>
            <div className="col-3">
                <EntitySelect type="buildings"
                              isMulti
                              placeholder="Tous les Bâtiment"
                              title="Bâtiment"
                              operationId={operationId} onChange={(value) => setSelectedBuildings(value)}
                              value={selectedBuildings}/>
            </div>
            <div className="col-3">
                <EntitySelect type="phase" isMulti={true} title="Phase" onChange={(value) => setSelectedPhases(value)}
                              placeholder="Toutes les Phases"
                              value={selectedPhases}/>
            </div>
        </div>

        <BYTable
            className="center-content"
            loading={loading}
            data={filteredLots}
            withSelect
            columns={['numeroLot', 'acquereur', 'nature', 'type', 'etage', 'batiment', 'totalReserves', 'pendingReserves', 'visiteCloison', 'visiteOpr', 'visitePreLivraison', 'visiteLivraison', 'dateRefusAdlr', 'refModeObtentionAdlr', 'dateEnvoiLvadlr', 'dateObtentionLvadlr']}
            columnNames={['Numéro', 'Client', 'Nature', 'Type', 'Étage', 'Bât.', 'Total Rés.', 'Nb restant', 'Visite Cloison', 'OPR/Réception', 'Prélivraison', 'Livraison',  'Quitus', 'Type Quitus', 'Date d\'envoi', 'Date d\'obtention']}
            dataTransform={{
                'acquereur': (lot) => lot.acquereur ? <div className="text-left">{[lot.acquereur?.prenom, lot.acquereur?.nom].join(' ')}</div> : '',
                'visiteCloison': (lot) => CommonHelper.formatDate(lot.visiteCloison),
                'visiteOpr': (lot) => CommonHelper.formatDate(lot.visiteOpr),
                'visitePreLivraison': (lot) => CommonHelper.formatDate(lot.visitePreLivraison),
                'visiteLivraison': (lot) => CommonHelper.formatDate(lot.visiteLivraison),
                'dateEnvoiLvadlr': (lot) => CommonHelper.formatDate(lot.dateEnvoiLvadlr),
                'dateObtentionLvadlr': (lot) => CommonHelper.formatDate(lot.dateObtentionLvadlr),
                'dateRefusAdlr': (lot) => lot.refModeObtentionAdlr ? 'Oui' : 'Non',
                'refModeObtentionAdlr': (lot) => CommonHelper.formatModeObtentionAdlr(lot.refModeObtentionAdlr)
            }}
            sortDataTransform={{
                'visiteCloison': (lot) => CommonHelper.dateStringToTime(lot.visiteCloison),
                'visiteOpr': (lot) => CommonHelper.dateStringToTime(lot.visiteOpr),
                'visitePreLivraison': (lot) => CommonHelper.dateStringToTime(lot.visitePreLivraison),
                'visiteLivraison': (lot) => CommonHelper.dateStringToTime(lot.visiteLivraison),
                'dateEnvoiLvadlr': (lot) => CommonHelper.dateStringToTime(lot.dateEnvoiLvadlr),
                'dateObtentionLvadlr': (lot) => CommonHelper.dateStringToTime(lot.dateObtentionLvadlr)
            }}
            onClick={(lot) => {
                window.open(`/operation/${lot.operationRefOperation}/lot/${lot.refLot}`);
            }}
            rowClassName={(lot) => {
                return lot.isDeletedInReferentiel ? 'table-danger' :  (lot.refModeObtentionAdlr ? 'table-success' : '');
            }}
            height={60}
            onRowSelect={(lots) => setSelectedLots(lots)}
            leftCustomView={<>
                <p className="mr-2">Exporter</p>
                <BYExportButtons
                    selectedReserves={selectedLots}
                    type="lot"
                    selectedPhases={selectedPhases}
                />
            </>}
        />

    </>;
};

export default LotList;
